import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  Swords, Gem, Store, Users, Trophy, QrCode, ArrowRight, Star, Zap,
  PawPrint, Car, Brain, Globe, TrendingUp, Heart, Shield, Sparkles,
  ChevronLeft, ChevronRight, Check, Play,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { ARENA, themeOf } from '../lib/classTheme';
import { Laurel, Torch, DustLayer, CrowdRow, GoldFrame } from '../components/ArenaFrame';

export default function Landing() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 400], [0, 80]);

  const { data: assets } = useQuery({
    queryKey: ['landingAssets'],
    queryFn: () => api.get('/templates/showcase').then(r => r.data).catch(() => null),
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* ═══════ NAV ═══════ */}
      <header className="sticky top-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative w-9 h-9 rounded-xl grid place-items-center font-display
              font-black text-lg text-white">
              <a href="https://wof.im"><img src="/uploads/logo-wof.webp" alt="Wall of Freedom"/></a>
            </div>
            <span className="font-display font-extrabold text-base tracking-wide">
              Wall of <span className="grad-text">Freedom</span>
            </span>
          </div>
          <div className="flex gap-2">
            <Link to="/login" className="btn-ghost btn-sm">Log in</Link>
            <Link to="/join" className="btn-primary btn-sm">Join free</Link>
          </div>
        </div>
        <div className="h-[2px]" style={{ background: ARENA.goldFrame, opacity: .5 }} />
      </header>

      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: ARENA.arenaDark }} />
        <CrowdRow className="top-0" opacity={0.25} />
        <DustLayer count={20} />
        <Torch className="left-[8%] top-24" size="lg" />
        <Torch className="right-[8%] top-24" size="lg" delay={0.7} />

        <motion.div className="relative max-w-6xl mx-auto px-5 pt-14 pb-20 text-center"
          style={{ y: heroY }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Laurel size={54} color={ARENA.goldL} className="mx-auto mb-3" />
            <span className="chip-gold mb-4 inline-flex">
              🏛️ 100% organic reach in your city
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: .12 }}
            className="font-display text-4xl sm:text-6xl font-black leading-[1.05] text-balance"
            style={{ color: ARENA.goldL, textShadow: '0 4px 30px rgba(201,151,58,.35)' }}>
            Shop local.<br />
            <span className="shimmer-gold">Forge your legend.</span><br />
            Rule the arena.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .25 }}
            className="mt-5 max-w-xl mx-auto text-balance text-base"
            style={{ color: 'rgba(232,220,200,.78)' }}>
            Every coin you spend at a local merchant becomes real power for your hero,
            your companion and your chariot. Free forever.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .35 }}
            className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/join" className="btn-primary btn-lg font-display">
              Create my hero <ArrowRight size={18} />
            </Link>
            <Link to="/join?role=producer" className="btn-lg font-display"
              style={{
                background: 'rgba(232,200,116,.12)', color: ARENA.goldL,
                border: `1px solid ${ARENA.gold}55`,
              }}>
              <Store size={17} /> I'm a business
            </Link>
          </motion.div>

          <LiveStats />
        </motion.div>

        <div className="h-[3px]" style={{ background: ARENA.goldFrame }} />
      </section>

      {/* ═══════ SHOWCASE SLIDERS ═══════ */}
      <section className="py-12" style={{ background: 'var(--sand-50)' }}>
        <div className="max-w-6xl mx-auto px-5 space-y-10">
          <Showcase title="Choose your champion" latin="Elige Heroem"
            subtitle="42 unique heroes across 6 classes"
            items={assets?.heroes} aspect="9/16" />
          <Showcase title="Bond with a companion" latin="Comes Fidelis"
            subtitle="30 loyal beasts that fight beside you"
            items={assets?.pets} aspect="9/16" />
          <Showcase title="Claim your chariot" latin="Currus"
            subtitle="30 racing machines for the Circus Maximus"
            items={assets?.cars} aspect="9/16" />
          <Showcase title="Forge legendary gear" latin="Arma"
            subtitle="150 items across 5 rarity tiers"
            items={assets?.items} aspect="1/1" small />
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section className="py-14" style={{ background: 'var(--marble)' }}>
        <div className="max-w-5xl mx-auto px-5">
          <SectionTitle title="How the loop works" latin="Circulus Vitae" />
          <div className="grid sm:grid-cols-4 gap-6 mt-8">
            {[
              { n: 1, t: 'Join your city', d: 'Pick your city — like #rome or #paris. 4,500 cities worldwide.', i: Globe },
              { n: 2, t: 'Shop & scan', d: 'The merchant registers your purchase. Points land instantly.', i: QrCode },
              { n: 3, t: 'Upgrade everything', d: '1 point = 1 stat. Hero, companion, chariot — all grow.', i: Zap },
              { n: 4, t: 'Fight & race', d: 'Duel friends, race in the Circus, climb the Hall of Fame.', i: Trophy },
            ].map((s, i) => (
              <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * .1 }} className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl grid place-items-center mb-3"
                  style={{ background: 'rgba(201,151,58,.14)', border: `1px solid ${ARENA.gold}44` }}>
                  <s.i size={24} style={{ color: ARENA.goldD }} />
                </div>
                <div className="chip-ink mb-2">STEP {s.n}</div>
                <h3 className="font-display font-bold">{s.t}</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--stone-500)' }}>{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FOR PLAYERS ═══════ */}
      <section className="py-14" style={{ background: 'var(--sand-100)' }}>
        <div className="max-w-5xl mx-auto px-5">
          <SectionTitle title="For players" latin="Pro Ludentibus" emoji="🎮" />
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            <Feature icon={Gem} title="Real rewards"
              desc="1 leu spent = 1 point = 1 stat point. Buy more at the same shop and your multiplier grows to 2.3×." />
            <Feature icon={Swords} title="Duel with QR"
              desc="Scan a friend's sigil. Pick your strike zone, guard zone and unleash your ULTIMATE." />
            <Feature icon={Car} title="Circus Maximus"
              desc="Race on 6 Roman circuits. Push, drift, nitro — outsmart your rival across 12 segments." />
            <Feature icon={PawPrint} title="Loyal companions"
              desc="Adopt a drake, fox, panther or lion. They assist, guard and heal you in battle." />
            <Feature icon={Brain} title="City trivia"
              desc="Answer questions about your city in 20 languages. Live rounds in the chat with friends." />
            <Feature icon={Users} title="Legions & referrals"
              desc="Form a legion, invite friends and earn 7% of everything they make — forever." />
          </div>
        </div>
      </section>

      {/* ═══════ FOR PARENTS ═══════ */}
      <section className="py-14" style={{ background: 'var(--marble)' }}>
        <div className="max-w-5xl mx-auto px-5">
          <SectionTitle title="For parents" latin="Pro Parentibus" emoji="👨‍👩‍👧" />
          <div className="card p-6 mt-8" style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
            <p className="font-display text-lg font-bold text-center mb-5">
              Your kids learn real skills while playing
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { i: '💰', t: 'Financial literacy', d: 'Points map 1:1 to real money. Kids learn the value of a purchase and how loyalty compounds.' },
                { i: '🌍', t: 'Geography & culture', d: '4,500 cities, 195 countries, trivia in 20 languages — learning disguised as play.' },
                { i: '🏪', t: 'Supporting local', d: 'Every reward comes from a real neighbourhood business. Community over corporations.' },
                { i: '🛡️', t: 'Safe by design', d: 'Moderated chats, no gambling, no loot boxes, no real-money purchases. Points only from real activity.' },
              ].map(x => (
                <div key={x.t} className="flex gap-3 p-3 rounded-xl" style={{ background: 'var(--sand-100)' }}>
                  <span className="text-2xl shrink-0">{x.i}</span>
                  <div>
                    <p className="font-display font-bold text-sm">{x.t}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--stone-500)' }}>{x.d}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 mt-5 text-xs font-bold"
              style={{ color: '#047857' }}>
              <Check size={14} /> No in-app purchases
              <span style={{ color: 'var(--stone-300)' }}>·</span>
              <Check size={14} /> No ads
              <span style={{ color: 'var(--stone-300)' }}>·</span>
              <Check size={14} /> No loot boxes
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FOR BUSINESSES ═══════ */}
      <section className="py-14 relative overflow-hidden" style={{ background: ARENA.arenaDark }}>
        <DustLayer count={14} />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="text-center mb-8">
            <Laurel size={40} color={ARENA.goldL} className="mx-auto mb-2" />
            <h2 className="font-display text-3xl font-black"
              style={{ color: ARENA.goldL, textShadow: '0 2px 20px rgba(201,151,58,.4)' }}>
              For businesses
            </h2>
            <p className="italic text-sm mt-1" style={{ color: 'rgba(232,220,200,.6)' }}>
              Pro Mercatoribus
            </p>
            <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: 'rgba(232,220,200,.78)' }}>
              Loyalty that actually works — because your customers <b style={{ color: ARENA.goldL }}>want</b> to come back.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { i: TrendingUp, t: '100% organic reach', d: 'Post an offer in your city group and every single member sees it. No algorithm, no ad spend, no boosting.' },
              { i: Users, t: 'Know your community', d: 'See exactly who buys, how often, how much and which tier they reached. Real customer analytics, free.' },
              { i: Heart, t: 'Loyalty that compounds', d: '6 tiers. The more they buy from you, the more points they earn — up to 2× per purchase. They stop shopping elsewhere.' },
              { i: Shield, t: 'Zero risk, zero cost', d: 'Free to join. No monthly fee. No commission. You only reward what was actually bought in your store.' },
            ].map(x => (
              <motion.div key={x.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-4 rounded-2xl"
                style={{ background: 'rgba(232,200,116,.08)', border: `1px solid ${ARENA.gold}33` }}>
                <div className="w-10 h-10 rounded-xl grid place-items-center mb-2"
                  style={{ background: 'rgba(232,200,116,.16)' }}>
                  <x.i size={19} style={{ color: ARENA.goldL }} />
                </div>
                <p className="font-display font-bold" style={{ color: ARENA.goldL }}>{x.t}</p>
                <p className="text-sm mt-1" style={{ color: 'rgba(232,220,200,.7)' }}>{x.d}</p>
              </motion.div>
            ))}
          </div>

          {/* mini dashboard mock */}
          <div className="mt-6 rounded-2xl overflow-hidden"
            style={{ padding: 2, background: ARENA.goldFrame }}>
            <div className="rounded-xl p-4" style={{ background: 'var(--marble)' }}>
              <p className="font-display font-bold text-sm mb-3">Your dashboard shows</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { l: 'Community', v: '284', s: '68% return' },
                  { l: 'Orders', v: '1,204', s: '+38% MoM' },
                  { l: 'Revenue', v: '48,320', s: 'tracked' },
                  { l: 'Points given', v: '61.4K', s: 'loyalty fuel' },
                ].map(k => (
                  <div key={k.l} className="rounded-xl p-2.5 text-center" style={{ background: 'var(--sand-100)' }}>
                    <p className="font-display font-black text-lg" style={{ color: ARENA.goldD }}>{k.v}</p>
                    <p className="text-[10px] font-display font-bold" style={{ color: 'var(--stone-500)' }}>{k.l}</p>
                    <p className="text-[9px]" style={{ color: 'var(--stone-400)' }}>{k.s}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-6">
            <Link to="/join?role=producer" className="btn-primary btn-lg font-display">
              <Store size={18} /> List my business — free
            </Link>
            <p className="text-[11px] mt-2" style={{ color: 'rgba(232,220,200,.55)' }}>
              We'll contact you within 24h to set everything up
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section className="py-14" style={{ background: 'var(--sand-50)' }}>
        <div className="max-w-3xl mx-auto px-5">
          <SectionTitle title="Questions" latin="Quaestiones" />
          <div className="space-y-2 mt-8">
            {[
              { q: 'Is it really free?', a: 'Yes. Free for players and free for businesses. No subscriptions, no commissions, no ads.' },
              { q: 'How do I earn points?', a: 'Mainly by shopping at registered local businesses — 1 point per currency unit spent, multiplied by your loyalty tier. Also from trivia, arena wins, races, sharing offers and referrals.' },
              { q: 'Can I buy points with money?', a: 'No. Never. Points only come from real activity — that is the whole point.' },
              { q: 'Is it safe for kids?', a: 'Yes. Moderated chats, no gambling, no loot boxes, no real-money purchases, no ads.' },
              { q: "I'm a business — what do I need?", a: 'Just sign up. Our team contacts you within 24h, sets up your loyalty program and shows you the dashboard. Takes 10 minutes.' },
              { q: 'Which cities are available?', a: 'Over 4,500 cities in 195 countries. If yours is missing, tell us and we add it.' },
            ].map((f, i) => <FaqItem key={i} {...f} />)}
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="py-14 relative overflow-hidden" style={{ background: ARENA.arenaDark }}>
        <DustLayer count={18} />
        <Torch className="left-[12%] top-10" size="lg" />
        <Torch className="right-[12%] top-10" size="lg" delay={.6} />
        <div className="relative max-w-3xl mx-auto px-5 text-center">
          <Laurel size={50} color={ARENA.goldL} className="mx-auto mb-3" />
          <h2 className="font-display text-3xl sm:text-4xl font-black"
            style={{ color: ARENA.goldL, textShadow: '0 3px 24px rgba(201,151,58,.4)' }}>
            Your loyalty is finally worth something
          </h2>
          <p className="mt-3 max-w-lg mx-auto" style={{ color: 'rgba(232,220,200,.75)' }}>
            Join thousands of players turning everyday shopping into an epic adventure.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/join" className="btn-primary btn-lg font-display">
              <Sparkles size={18} /> Start free — get 100 points
            </Link>
            <Link to="/join?role=producer" className="btn-lg font-display"
              style={{ background: 'rgba(232,200,116,.12)', color: ARENA.goldL,
                       border: `1px solid ${ARENA.gold}55` }}>
              <Store size={17} /> List my business
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-sm" style={{ color: 'var(--stone-400)' }}>
        <Laurel size={26} color="var(--stone-300)" className="mx-auto mb-2" />
        © {new Date().getFullYear()} Wall of Freedom · WoF.IM
      </footer>
    </div>
  );
}

/* ---------------- SUB ---------------- */
function LiveStats() {
  const { data } = useQuery({
    queryKey: ['publicStats'],
    queryFn: () => api.get('/stats/public').then(r => r.data).catch(() => null),
  });

  const stats = [
    { l: 'Cities', v: data?.cities ? compact(data.cities) : '4,500+' },
    { l: 'Countries', v: data?.countries || '195' },
    { l: 'Heroes', v: data?.heroes ? compact(data.heroes) : '42' },
    { l: 'Merchants', v: data?.shops ? compact(data.shops) : 'Growing' },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .5 }}
      className="mt-10 grid grid-cols-4 gap-2 max-w-lg mx-auto">
      {stats.map(s => (
        <div key={s.l} className="rounded-xl px-2 py-2.5 text-center"
          style={{ background: 'rgba(232,200,116,.10)', border: `1px solid ${ARENA.gold}28` }}>
          <p className="font-display font-black text-lg" style={{ color: ARENA.goldL }}>{s.v}</p>
          <p className="text-[9px] font-display font-bold tracking-wider"
            style={{ color: 'rgba(232,220,200,.55)' }}>{s.l.toUpperCase()}</p>
        </div>
      ))}
    </motion.div>
  );
}

function Showcase({ title, latin, subtitle, items, aspect = '9/16', small }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);

  const list = items?.length ? items : Array.from({ length: 8 }).map((_, i) => ({ id: i, skeleton: true }));

  const scroll = (dir) => {
    const el = ref.current; if (!el) return;
    const w = small ? 120 : 168;
    el.scrollBy({ left: dir * w * 2, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-3">
        <div>
          <h3 className="font-display text-xl font-extrabold text-engraved">{title}</h3>
          <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-500)' }}>
            <span className="italic opacity-80">{latin}</span> · {subtitle}
          </p>
        </div>
        <div className="hidden sm:flex gap-1">
          <button onClick={() => scroll(-1)} className="btn-ghost btn-sm !px-2">
            <ChevronLeft size={15} />
          </button>
          <button onClick={() => scroll(1)} className="btn-ghost btn-sm !px-2">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      <div ref={ref} className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
        {list.map((x, i) => (
          <motion.div key={x.id || i}
            initial={{ opacity: 0, scale: .92 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }} transition={{ delay: (i % 8) * .04 }}
            whileHover={{ y: -6 }}
            className={`shrink-0 rounded-2xl overflow-hidden ${small ? 'w-[104px]' : 'w-[150px]'}`}
            style={{ padding: 2, background: ARENA.goldFrame }}>
            <div className="rounded-xl overflow-hidden relative" style={{ background: 'var(--stone-200)' }}>
              <div style={{ aspectRatio: aspect }}>
                {x.skeleton
                  ? <div className="skeleton w-full h-full" />
                  : <img src={x.thumb || x.image} alt={x.name}
                      className="w-full h-full object-cover object-top" loading="lazy" />}
              </div>
              {!x.skeleton && x.name && (
                <div className="absolute inset-x-0 bottom-0 p-1.5"
                  style={{ background: 'linear-gradient(180deg,transparent,rgba(36,30,22,.9))' }}>
                  <p className="text-[9px] font-display font-black text-white truncate">{x.name}</p>
                  {x.subtitle && (
                    <p className="text-[8px] text-white/60 truncate">{x.subtitle}</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function SectionTitle({ title, latin, emoji }) {
  return (
    <div className="text-center">
      {emoji && <div className="text-3xl mb-2">{emoji}</div>}
      <Laurel size={34} color={ARENA.gold} className="mx-auto mb-1" />
      <h2 className="font-display text-3xl font-black text-engraved">{title}</h2>
      {latin && <p className="italic text-sm mt-0.5" style={{ color: 'var(--stone-400)' }}>{latin}</p>}
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} className="card p-5">
      <div className="w-11 h-11 rounded-xl grid place-items-center mb-3"
        style={{ background: 'rgba(201,151,58,.14)' }}>
        <Icon size={20} style={{ color: ARENA.goldD }} />
      </div>
      <h3 className="font-display font-bold">{title}</h3>
      <p className="text-sm mt-1" style={{ color: 'var(--stone-500)' }}>{desc}</p>
    </motion.div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card overflow-hidden">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <span className="font-display font-bold text-sm">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} className="text-xl shrink-0"
          style={{ color: ARENA.goldD }}>+</motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <p className="px-4 pb-4 text-sm" style={{ color: 'var(--stone-600)' }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}